import { type SelectOption, useToaster } from '@innovationdepartment/proxima-ui';
import { useShowSpinner } from 'hooks';
import useBrandTagsApi from 'hooks/useBrandTagsApi';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { BrandTag, UpdateTagDto } from 'types/brandTag';
import AddNewCategoryModal from './AddNewCategoryModal';
import BrandTagDetailView from './BrandTagDetail.View';
import DeleteTagConfirmationModal from './DeleteTagConfirmationModal';

const ADD_NEW_CATEGORY = '__ADD_NEW_CATEGORY__';
const ADD_NEW_SUBCATEGORY = '__ADD_NEW_SUBCATEGORY__';

const BrandTagDetailContainer = () => {
  const { tagId } = useParams<{ tagId: string }>();
  const navigate = useNavigate();
  const { showToaster } = useToaster();
  const {
    fetchTagById,
    updateTag,
    deleteTag,
    getCategories,
    getSubCategories,
    getGenders,
    loading
  } = useBrandTagsApi();

  const [originalTag, setOriginalTag] = useState<BrandTag | null>(null);
  const [editedTag, setEditedTag] = useState<BrandTag | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [hasBrandsUsingTag, setHasBrandsUsingTag] = useState(true);
  const [categories, setCategories] = useState<string[]>([]);
  const [subCategories, setSubCategories] = useState<string[]>([]);
  const [genders, setGenders] = useState<string[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<SelectOption[]>([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState<SelectOption[]>([]);
  const [genderOptions, setGenderOptions] = useState<SelectOption[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [showModal, setShowModal] = useState<'category' | 'subcategory' | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [errors, setErrors] = useState<Set<string>>(new Set());

  useShowSpinner({ show: loading });

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    if (!tagId) return;

    const loadAllData = async () => {
      const [fetchedTag, fetchedCategories, fetchedGenders] = await Promise.all([
        fetchTagById(tagId),
        getCategories(),
        getGenders()
      ]);
      // if (!isMountedRef.current) return;

      if (!fetchedTag) {
        showToaster({ message: `Tag with id :${tagId} not found`, variant: 'error' });
        navigate('/brand-tags');
        return;
      }

      const fetchedSubcategories = await getSubCategories(fetchedTag.category);
      // Set all state at once to avoid race conditions
      setOriginalTag(fetchedTag);
      setEditedTag(fetchedTag);
      setCategories(fetchedCategories);
      setSubCategories(fetchedSubcategories);
      setGenders(fetchedGenders);
      setDataLoaded(true);
    };

    setDataLoaded(false);
    loadAllData();
  }, [tagId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    if (!editedTag?.category || !originalTag) {
      return;
    }

    const loadSubCategoriesForCategory = async () => {
      const subCats = await getSubCategories(editedTag.category);
      setSubCategories(subCats);
    };

    loadSubCategoriesForCategory();
  }, [editedTag?.category]);

  const handleFieldChange = (field: keyof BrandTag, value: string | boolean) => {
    if (!editedTag) return;

    setEditedTag({
      ...editedTag,
      [field]: value
    });
  };

  const handleCategoryChange = async (value: string) => {
    if (!editedTag) return;

    if (value === ADD_NEW_CATEGORY) {
      setShowModal('category');
    } else {
      setEditedTag({
        ...editedTag,
        category: value,
        subCategory: '' // Reset subcategory when category changes
      });
    }
  };

  const handleSubCategoryChange = async (value: string) => {
    if (!editedTag) return;

    if (value === ADD_NEW_SUBCATEGORY) {
      setShowModal('subcategory');
    } else {
      setEditedTag({
        ...editedTag,
        subCategory: value
      });
    }
  };

  useEffect(() => {
    if (!originalTag || !editedTag) {
      setIsDirty(false);
      return;
    }
    setIsDirty(JSON.stringify(originalTag) !== JSON.stringify(editedTag));
  }, [originalTag, editedTag]);

  useEffect(() => {
    setCategoryOptions([
      ...categories.map(cat => ({ label: cat, value: cat })),
      { label: '+ Add New Category', value: ADD_NEW_CATEGORY }
    ]);
  }, [categories]);

  useEffect(() => {
    setSubCategoryOptions([
      ...subCategories.filter(sub => sub !== '').map(sub => ({ label: sub, value: sub })),
      { label: '+ Add New Sub-Category', value: ADD_NEW_SUBCATEGORY }
    ]);
  }, [subCategories]);

  useEffect(() => {
    setGenderOptions(genders.map(gender => ({ label: gender, value: gender })));
  }, [genders]);

  const handleModalSubmit = (newValue: string) => {
    if (!editedTag) return;

    if (showModal === 'category') {
      setCategories([...categories, newValue]);
      setEditedTag({
        ...editedTag,
        category: newValue,
        subCategory: '' // Reset subcategory when category changes
      });
    } else if (showModal === 'subcategory') {
      setSubCategories([...subCategories, newValue]);
      setEditedTag({
        ...editedTag,
        subCategory: newValue
      });
    }

    setShowModal(null);
  };

  const handleModalCancel = () => {
    setShowModal(null);
  };

  const validateForm = (): boolean => {
    if (!editedTag) return false;

    const newErrors = new Set<string>();

    const requiredFields: (keyof BrandTag)[] = [
      'originalName',
      'displayName',
      'category',
      'description',
      'subCategory',
      'genderRelevance',
      'typeDisplayName',
      'typeHelperText'
    ];

    requiredFields.forEach(field => {
      const value = editedTag[field as keyof BrandTag];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        newErrors.add(field);
      }
    });

    setErrors(newErrors);
    return newErrors.size === 0;
  };

  const handleSave = async () => {
    if (!editedTag || !tagId) return;

    if (!validateForm()) {
      showToaster({
        message: 'Please fill in all required fields',
        variant: 'error'
      });
      return;
    }

    const updateData: UpdateTagDto = {
      displayName: editedTag.displayName,
      description: editedTag.description,
      category: editedTag.category,
      subCategory: editedTag.subCategory,
      genderRelevance: editedTag.genderRelevance,
      typeDisplayName: editedTag.typeDisplayName,
      typeHelperText: editedTag.typeHelperText,
      brandAssignmentGuidance: editedTag.brandAssignmentGuidance,
      personaRelevanceGuidance: editedTag.personaRelevanceGuidance,
      personaEligible: editedTag.personaEligible,
      sensitive: editedTag.sensitive,
      deprecated: editedTag.deprecated
    };

    const updatedTag = await updateTag(tagId, updateData);

    if (updatedTag) {
      setOriginalTag(updatedTag);
      setEditedTag(updatedTag);
    }
    setErrors(new Set()); // Clear errors on successful save

    showToaster({
      message: 'Tag updated successfully',
      variant: 'success'
    });
  };

  const handleReset = async () => {
    if (!originalTag) {
      // Should not happen.
      // If no original tag, navigate back to reload
      navigate(`/brand-tags/${tagId}`);
      return;
    }
    setEditedTag(originalTag);
    setErrors(new Set());
  };

  const handleBack = () => {
    navigate('/brand-tags');
  };

  const handleDelete = () => {
    if (hasBrandsUsingTag) {
      showToaster({
        message: 'Cannot delete tag that is being used by brands',
        variant: 'error'
      });
      return;
    }
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (!tagId) return;
    await deleteTag(tagId);
    showToaster({
      message: 'Brand Tag deleted successfully',
      variant: 'success'
    });
    setShowDeleteConfirmation(false);
    navigate('/brand-tags');
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  const handleBrandCountChange = (count: number) => {
    setHasBrandsUsingTag(count > 0);
  };

  return (
    <>
      <BrandTagDetailView
        tag={originalTag}
        editedTag={editedTag}
        errors={errors}
        onFieldChange={handleFieldChange}
        onBack={handleBack}
        onSave={handleSave}
        onReset={handleReset}
        onDelete={handleDelete}
        isDirty={isDirty}
        hasBrandsUsingTag={hasBrandsUsingTag}
        categoryOptions={categoryOptions}
        subCategoryOptions={subCategoryOptions}
        genderOptions={genderOptions}
        onCategoryChange={handleCategoryChange}
        onSubCategoryChange={handleSubCategoryChange}
        dataLoaded={dataLoaded}
        onBrandCountChange={handleBrandCountChange}
      />
      <AddNewCategoryModal
        show={showModal !== null}
        type={showModal || 'category'}
        onCancel={handleModalCancel}
        onSubmit={handleModalSubmit}
      />
      <DeleteTagConfirmationModal
        open={showDeleteConfirmation}
        tagDisplayName={editedTag?.displayName || ''}
        close={handleCancelDelete}
        confirm={handleConfirmDelete}
        isLoading={loading}
      />
    </>
  );
};

export default BrandTagDetailContainer;
